import type { FreeScoutPreview, Me } from "@hoffman-support/contracts";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { ApiError } from "../api";
import {
  canUseFreeScout,
  emptySelection,
  type FreeScoutMapping,
  freeScoutMappings,
  freeScoutPreview,
  freeScoutRepair,
  freeScoutRoute,
  type HandoffSelection,
  handoffError,
  handoffReady,
} from "../freescout-api";
import { FreeScoutSelection, FreeScoutSource, sourceDate } from "./FreeScoutSource";

const LINK_STATE: Record<FreeScoutMapping["syncState"], string> = {
  unlinked: "Not linked",
  linked: "Linked",
  needs_review: "Needs review",
  unavailable: "Conversation unavailable",
};

export function canRepairFreeScout(me: Me, mapping: FreeScoutMapping) {
  return (
    me.user.roles.includes("admin") &&
    (mapping.syncState === "needs_review" || mapping.syncState === "unavailable")
  );
}

export function FreeScoutMappings({
  me,
  requestId,
  requesterId,
}: {
  me: Me;
  requestId: string;
  requesterId: string | null;
}) {
  if (!canUseFreeScout(me)) return null;
  return <Mappings me={me} requestId={requestId} requesterId={requesterId} />;
}

export function FreeScoutMappingSummary({ mapping }: { mapping: FreeScoutMapping }) {
  return (
    <>
      <p>
        <a
          href={`/staff/freescout/${mapping.instanceId}/${mapping.mailboxId}/${mapping.conversationId}`}
        >
          FreeScout conversation #{mapping.conversationId}
        </a>{" "}
        · Message #{mapping.sourceThreadId}
      </p>
      <p>
        <strong>{LINK_STATE[mapping.syncState]}</strong> · Last checked:{" "}
        {sourceDate(mapping.checkedAt)}
      </p>
      {mapping.syncState === "needs_review" || mapping.syncState === "unavailable" ? (
        <p>
          The source conversation changed or could not be checked. An administrator must verify it
          before repairing the link.
        </p>
      ) : null}
    </>
  );
}

function Mappings({
  me,
  requestId,
  requesterId,
}: {
  me: Me;
  requestId: string;
  requesterId: string | null;
}) {
  const [mappings, setMappings] = useState<FreeScoutMapping[] | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [repairId, setRepairId] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload fetches the current mapping version after repair or an operator request.
  useEffect(() => {
    let active = true;
    setMappings(null);
    setError("");
    freeScoutMappings(requestId)
      .then((result) => {
        if (active) setMappings(result.mappings);
      })
      .catch((error) => {
        if (active) setError(handoffError(error, "Email source links could not be loaded."));
      });
    return () => {
      active = false;
    };
  }, [requestId, reload]);

  return (
    <section
      aria-labelledby="email-source-heading"
      style={{ borderTop: "1px solid #ddd", marginTop: "1.5rem" }}
    >
      <h3 id="email-source-heading">Email source</h3>
      <p>
        <button
          type="button"
          disabled={Boolean(repairId)}
          onClick={() => {
            setNotice("");
            setReload((value) => value + 1);
          }}
        >
          Reload source links
        </button>
      </p>
      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {!mappings && !error ? <p role="status">Loading source links…</p> : null}
      {mappings?.length === 0 ? <p>No FreeScout conversation is linked to this request.</p> : null}
      {mappings?.map((mapping) => (
        <div
          key={`${mapping.id}-${mapping.version}`}
          style={{ border: "1px solid #ddd", padding: "0.75rem", marginBottom: "1rem" }}
        >
          <FreeScoutMappingSummary mapping={mapping} />
          {canRepairFreeScout(me, mapping) ? (
            repairId === mapping.id ? (
              <Repair
                me={me}
                mapping={mapping}
                requesterId={requesterId}
                onCancel={() => setRepairId(null)}
                onSaved={() => {
                  setRepairId(null);
                  setNotice("Email source link repaired.");
                  setReload((value) => value + 1);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  setRepairId(mapping.id);
                  setNotice("");
                }}
              >
                Review and repair link
              </button>
            )
          ) : null}
        </div>
      ))}
    </section>
  );
}

function Repair({
  me,
  mapping,
  requesterId,
  onCancel,
  onSaved,
}: {
  me: Me;
  mapping: FreeScoutMapping;
  requesterId: string | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [conversation, setConversation] = useState(String(mapping.conversationId));
  const [preview, setPreview] = useState<FreeScoutPreview | null>(null);
  const [selection, setSelection] = useState<HandoffSelection>(emptySelection);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewVersion = useRef(0);
  const reference = freeScoutRoute(
    `/staff/freescout/${mapping.instanceId}/${mapping.mailboxId}/${conversation}`,
  );

  async function loadPreview() {
    if (!reference || loading || saving) return;
    const version = ++previewVersion.current;
    setLoading(true);
    setPreview(null);
    setSelection(emptySelection());
    setNotice("");
    try {
      const found = await freeScoutPreview(reference);
      if (version !== previewVersion.current) return;
      if (found.status.requests.some((request) => request.id !== mapping.requestId)) {
        setNotice(
          "This conversation is already linked to a different request. Check the conversation number.",
        );
        return;
      }
      setPreview(found);
    } catch (error) {
      if (version === previewVersion.current)
        setNotice(handoffError(error, "The conversation could not be checked."));
    } finally {
      if (version === previewVersion.current) setLoading(false);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!preview || !handoffReady(preview, selection) || saving) return;
    setSaving(true);
    setNotice("");
    try {
      await freeScoutRepair(me, mapping, preview, selection, requesterId);
      onSaved();
    } catch (error) {
      setNotice(
        error instanceof ApiError && error.failure.status === 409
          ? "The link changed while you were working. Cancel this repair and reload source links, then review it again."
          : handoffError(error, "The link could not be repaired. Your selections are kept."),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} aria-label="Repair email source link">
      <p>
        Check the correct conversation and verify the account that owns this request. A conversation
        move or merge is never followed automatically.
      </p>
      <label htmlFor={`repair-conversation-${mapping.id}`}>
        Correct FreeScout conversation number *
      </label>
      <br />
      <input
        id={`repair-conversation-${mapping.id}`}
        type="number"
        min={1}
        max={2_147_483_647}
        step={1}
        value={conversation}
        disabled={saving}
        onChange={(event) => {
          previewVersion.current++;
          setLoading(false);
          setConversation(event.target.value);
          setPreview(null);
          setSelection(emptySelection());
          setNotice("");
        }}
      />{" "}
      <button
        type="button"
        disabled={!reference || loading || saving}
        onClick={() => void loadPreview()}
      >
        Check conversation
      </button>
      {loading ? <p role="status">Checking the conversation…</p> : null}
      {preview ? (
        <>
          <FreeScoutSource preview={preview} />
          <FreeScoutSelection
            key={previewVersion.current}
            preview={preview}
            value={selection}
            onChange={setSelection}
            disabled={saving}
          />
        </>
      ) : null}
      {selection.requester && selection.requester.id !== requesterId ? (
        <p role="alert">Select the account that owns this request.</p>
      ) : null}
      {notice ? <p role="alert">{notice}</p> : null}
      <p>
        <button
          type="submit"
          disabled={
            saving ||
            !preview ||
            !handoffReady(preview, selection) ||
            selection.requester?.id !== requesterId
          }
        >
          {saving ? "Saving…" : "Repair link"}
        </button>{" "}
        <button type="button" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </p>
    </form>
  );
}
